import React from 'react';
import { css } from '@emotion/react';
import { i18n_add_classes_to_your_library_and_calendar_create_playlists_set_and_reach_goals_and_more } from '@yi/i18n/phrases/i18n_add_classes_to_your_library_and_calendar_create_playlists_set_and_reach_goals_and_more';
import { i18n_customize_your_practice } from '@yi/i18n/phrases/i18n_customize_your_practice';
import { i18n_deep_study } from '@yi/i18n/phrases/i18n_deep_study';
import { i18n_diverse_class_offerings } from '@yi/i18n/phrases/i18n_diverse_class_offerings';
import { i18n_embrace_balanced_approach_to_wellness_addressing_physical_mental_emotional_and_spiritual_for } from '@yi/i18n/phrases/i18n_embrace_balanced_approach_to_wellness_addressing_physical_mental_emotional_and_spiritual_for';
import { i18n_from_prenatal_yoga_to_yoga_nidra_our_platform_caters_to_diverse_interests_and_levels_ensuring } from '@yi/i18n/phrases/i18n_from_prenatal_yoga_to_yoga_nidra_our_platform_caters_to_diverse_interests_and_levels_ensuring';
import { i18n_go_beyond_physical_practice_of_yoga_with_our_robust_library_of_courses_articles_and_e_books } from '@yi/i18n/phrases/i18n_go_beyond_physical_practice_of_yoga_with_our_robust_library_of_courses_articles_and_e_books';
import { i18n_holistic_approach } from '@yi/i18n/phrases/i18n_holistic_approach';
import { i18n_why_yoga_international } from '@yi/i18n/phrases/i18n_why_yoga_international';

import { getOptimizedImgSrc } from '~/src/common/image';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, fontWeight } from '~/src/styles';
import { paddingVariables, typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';

import { ReactComponent as CustomizeYourPracticeIcon } from './customize-your-practice.svg';
import { ReactComponent as DeepStudyIcon } from './deep-study.svg';
import { ReactComponent as DiverseClassOfferingsIcon } from './diverse-class-offerings.svg';
import { ReactComponent as HolisticApproachIcon } from './holistic-approach.svg';
import { ReactComponent as Mandala } from './mandala.svg';
import { commonStyles } from '../common-styles';
import { StartFreeTrialButton } from '../StartFreeTrialButton';

const commonIconStyles = css({
  marginBottom: '8px',
});

const commonBlockStyles = css({
  gap: '8px',
  padding: '24px 0',
  borderBottom: '1px solid  #d3d3d3',
  '&:first-of-type': {
    paddingTop: '0',
  },
  '&:last-of-type': {
    borderBottom: 'none',
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  [breakpoints.minTablet]: {
    padding: '32px 0',
  },
  [breakpoints.minDesktop]: {
    gap: '16px',
    borderBottom: 'none',
    alignItems: 'start',
    padding: '0',
    textAlign: 'left',
  },
  h2: css(typography.bodyText, resetStyles.heading, {
    fontWeight: fontWeight.bold,
    color: 'var(--Dark-Text, #27292D)',
    [breakpoints.minDesktop]: typography.heading4,
  }),
  h3: css(typography.bodyText, resetStyles.heading, {
    color: 'var(--Greay-Text, #585858)',
    [breakpoints.minDesktop]: typography.subHeading1,
  }),
});

const MAX_IMG_WIDTH = 360;

const styles = {
  startFreeTrialButton: css(commonStyles.contentPadding, {
    gridArea: 'button',
  }),
  background: css({
    zIndex: 1,
    gridArea: 'background',
    borderRadius: 'var(--Border-Radius)',
    background: 'var(--Primary-Background, rgba(255, 243, 226, 0.50))',
    gridColumnStart: '1',
    gridRowStart: '2',
    gridRowEnd: '-1',
  }),
  img: css({
    gridArea: 'img',
    zIndex: '10',
    alignSelf: 'end',
    justifySelf: 'center',
    width: '64%',
    maxWidth: `${MAX_IMG_WIDTH}px`,
  }),
  mandala: css({
    gridColumnStart: '1',
    [breakpoints.minDesktop]: {
      gridColumnStart: '2',
    },
    gridRowStart: '1',
    gridRowEnd: '2',
    zIndex: 10,
    justifySelf: 'center',
    width: '90%',
    maxWidth: '495px',
  }),
  list: css(commonStyles.contentPadding, {
    zIndex: '10',
    gridArea: 'list',
    [breakpoints.minDesktop]: {
      display: 'grid',
      gridTemplateAreas: `'deepStudy customizeYourPractice' 'holisticApproach diverseClassOfferings'`,
      gridTemplateColumns: `1fr 1fr`,
      columnGap: paddingVariables.p120,
      rowGap: paddingVariables.p80,
    },
  }),
  deepStudyBlock: css(commonBlockStyles, {
    [breakpoints.minDesktop]: {
      gridArea: 'deepStudy',
    },
  }),
  diverseClassOfferingsBlock: css(commonBlockStyles, {
    [breakpoints.minDesktop]: {
      gridArea: 'diverseClassOfferings',
    },
  }),
  holisticApproachBlock: css(commonBlockStyles, {
    [breakpoints.minDesktop]: {
      gridArea: 'holisticApproach',
    },
  }),
  customizeYourPracticeBlock: css(commonBlockStyles, {
    paddingBottom: 0,
    [breakpoints.minTablet]: {
      paddingBottom: 0,
    },
    [breakpoints.minDesktop]: {
      gridArea: 'customizeYourPractice',
    },
  }),
  container: css({
    display: 'grid',
    gridTemplateAreas: `'img' 'img' '.' 'title' '.' 'list' '.' 'button' '.'`,
    gridTemplateRows: `4fr 2.8fr ${paddingVariables.p24} auto ${paddingVariables.p40} auto var(--Section-Padding) auto var(--Section-Padding)`,
    [breakpoints.minTablet]: {
      gridTemplateRows: `4fr 2.8fr ${paddingVariables.p56} auto ${paddingVariables.p40} auto ${paddingVariables.p80} auto ${paddingVariables.p80}`,
    },
    [breakpoints.minDesktop]: {
      gridTemplateAreas: `'. img .' '. img .' '. . .' '. title .' '. . .' '. list .' '. . .' '. button .' '. . .'`,
      gridTemplateRows: `4fr 2.8fr ${paddingVariables.p40} auto ${paddingVariables.p80} auto ${paddingVariables.p120} auto ${paddingVariables.p120}`,
      gridTemplateColumns: `1fr var(--Max-Desktop-Width) 1fr`,
    },
  }),
  heading: css(commonStyles.contentPadding, typography.heading1, commonStyles.containerMaxWidth, {
    zIndex: 10,
    gridArea: 'title',
    color: 'var(--Dark-Text, #27292D)',
    justifySelf: 'center',
    i: {
      fontSize: '30px',
      lineHeight: '40px',
      letterSpacing: '1.5px',
      [breakpoints.minTablet]: {
        fontSize: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      },
    },
  }),
  deepStudyIcon: css(commonIconStyles, {
    height: '34px',
    [breakpoints.minTablet]: {
      height: '52px',
    },
    [breakpoints.minDesktop]: {
      height: '76px',
    },
  }),
  customizeYourPracticeIcon: css(commonIconStyles, {
    height: '40px',
    [breakpoints.minTablet]: {
      height: '52px',
    },
    [breakpoints.minDesktop]: {
      height: '76px',
    },
  }),
  holisticApproachIcon: css(commonIconStyles, {
    height: '32px',
    [breakpoints.minTablet]: {
      height: '52px',
    },
    [breakpoints.minDesktop]: {
      height: '76px',
    },
  }),
  diverseClassOfferingsIcon: css(commonIconStyles, {
    height: '48px',
    [breakpoints.minTablet]: {
      height: '52px',
    },
    [breakpoints.minTablet]: {
      height: '76px',
    },
  }),
};

const IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/092339fa-3bfa-4a46-a36c-4815e298b898?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

export const TheYiDifference: React.FC = () => {
  const { locale } = useLocale();

  return (
    <section css={styles.container}>
      <div css={styles.background} />
      <Mandala css={styles.mandala} />
      <img
        css={styles.img}
        src={getOptimizedImgSrc({ src: IMG_SRC, width: MAX_IMG_WIDTH })}
        loading="lazy"
        decoding="async"
      />

      <h1 css={styles.heading}>{i18n_why_yoga_international(locale)}</h1>
      <div css={styles.list}>
        <div css={styles.deepStudyBlock}>
          <DeepStudyIcon css={styles.deepStudyIcon} />
          <h2>{i18n_deep_study(locale)}</h2>
          <h3>
            {i18n_go_beyond_physical_practice_of_yoga_with_our_robust_library_of_courses_articles_and_e_books(
              locale,
            )}
          </h3>
        </div>
        <div css={styles.diverseClassOfferingsBlock}>
          <DiverseClassOfferingsIcon css={styles.diverseClassOfferingsIcon} />
          <h2>{i18n_diverse_class_offerings(locale)}</h2>
          <h3>
            {i18n_from_prenatal_yoga_to_yoga_nidra_our_platform_caters_to_diverse_interests_and_levels_ensuring(
              locale,
            )}
          </h3>
        </div>
        <div css={styles.holisticApproachBlock}>
          <HolisticApproachIcon css={styles.holisticApproachIcon} />
          <h2>{i18n_holistic_approach(locale)}</h2>
          <h3>
            {i18n_embrace_balanced_approach_to_wellness_addressing_physical_mental_emotional_and_spiritual_for(
              locale,
            )}
          </h3>
        </div>
        <div css={styles.customizeYourPracticeBlock}>
          <CustomizeYourPracticeIcon css={styles.customizeYourPracticeIcon} />
          <h2>{i18n_customize_your_practice(locale)}</h2>
          <h3>
            {i18n_add_classes_to_your_library_and_calendar_create_playlists_set_and_reach_goals_and_more(
              locale,
            )}
          </h3>
        </div>
      </div>
      <StartFreeTrialButton css={styles.startFreeTrialButton} />
    </section>
  );
};
